import { get, set } from '../node_modules/idb-keyval/dist/index.js';
import config from '/config.js';
import { withTiming } from '/utils.js';

/** @type {unknown} */
let _opfsRoot = null;
/** @type {Error | null} */
let _opfsError = null;
/** @type {Promise<{ root: unknown; error: Error | null }> | null} */
let _opfsResolvePromise = null;

/**
 * Single shared OPFS root resolution per page (dedupes getDirectory across the storage probes, the marker
 * prewarm and every collect).
 * @returns {Promise<{ root: unknown; error: Error | null }>}
 */
export function resolveOpfsRootOnce() {
    if (_opfsResolvePromise) return _opfsResolvePromise;
    _opfsResolvePromise = (async () => {
        if (typeof navigator === 'undefined' || typeof navigator.storage?.getDirectory !== 'function') {
            return { root: null, error: null };
        }
        try {
            _opfsRoot = await navigator.storage.getDirectory();
            _opfsError = null;
            return { root: _opfsRoot, error: null };
        } catch (e) {
            _opfsError = e instanceof Error ? e : new Error(String(e));
            _opfsRoot = null;
            return { root: null, error: _opfsError };
        }
    })();
    return _opfsResolvePromise;
}

function opfsErrorTag(error) {
    if (!error) return null;
    const msg = typeof error.message === 'string' ? error.message : String(error);
    if (msg.includes('unknown transient reason')) return 'transient';
    if (msg.includes('Security error')) return 'security';
    return 'other';
}

/** Same behavior as former ensureIndexedDBTestOpened. */
function openIndexedDbTest() {
    return new Promise((resolve) => {
        try {
            const req = indexedDB.open('test');
            req.onerror = () => resolve(false);
            req.onsuccess = () => resolve(true);
            req.onblocked = () => resolve(true);
        } catch (e) {
            resolve(false);
        }
    });
}

/** Safari-style IndexedDB + Blob in object store (when OPFS getDirectory is unavailable). */
function probeIndexedDbBlobStore() {
    return new Promise((resolve) => {
        const tmp = String(Math.random());
        try {
            const dbReq = indexedDB.open(tmp, 1);
            dbReq.onupgradeneeded = (ev) => {
                const db = ev.target.result;
                let finished = false;
                const done = (payload) => {
                    if (finished) return;
                    finished = true;
                    try {
                        db.close();
                    } catch (e) { /* ignore */ }
                    try {
                        indexedDB.deleteDatabase(tmp);
                    } catch (e) { /* ignore */ }
                    resolve(payload);
                };
                try {
                    db.createObjectStore('t', { autoIncrement: true }).put(new Blob());
                    done({ blobStoreWritable: true, blobStoreNotYetSupported: false });
                } catch (err) {
                    const message = err instanceof Error ? err.message : String(err);
                    done({
                        blobStoreWritable: false,
                        blobStoreNotYetSupported: message.includes('are not yet supported'),
                    });
                }
            };
            dbReq.onerror = () => resolve({ blobStoreWritable: null, blobStoreNotYetSupported: false });
        } catch (e) {
            resolve({ blobStoreWritable: null, blobStoreNotYetSupported: false });
        }
    });
}

/**
 * Returns `window.localStorage` or null. Reading the property can throw SecurityError
 * (cross-origin iframe, sandbox, blocked third-party storage, etc.).
 * @returns {Storage | null}
 */
export function getLocalStorageSafe() {
    try {
        if (typeof window === 'undefined') return null;
        return window.localStorage;
    } catch (e) {
        return null;
    }
}

/** @returns {string | null} */
export function localStorageGetItemSafe(key) {
    try {
        const ls = getLocalStorageSafe();
        if (ls == null) return null;
        return ls.getItem(key);
    } catch (e) {
        return null;
    }
}

/** @returns {boolean} true if set succeeded */
export function localStorageSetItemSafe(key, value) {
    try {
        const ls = getLocalStorageSafe();
        if (ls == null) return false;
        ls.setItem(key, value);
        return true;
    } catch (e) {
        return false;
    }
}

/** @returns {boolean} true if remove succeeded */
export function localStorageRemoveItemSafe(key) {
    try {
        const ls = getLocalStorageSafe();
        if (ls == null) return false;
        ls.removeItem(key);
        return true;
    } catch (e) {
        return false;
    }
}

/** Legacy Web SQL probe (openDatabase). */
function probeOpenDatabase() {
    const openDB = typeof window !== 'undefined' ? window.openDatabase : null;
    if (typeof openDB !== 'function') {
        return { available: false, openThrows: null };
    }
    try {
        openDB.call(window, null, null, null, null);
        return { available: true, openThrows: false };
    } catch (e) {
        return { available: true, openThrows: true };
    }
}

function probeLocalStorageWritable() {
    try {
        const ls = getLocalStorageSafe();
        if (ls == null) return { writable: null };
        ls.setItem('__rs_ls__', '1');
        ls.removeItem('__rs_ls__');
        return { writable: true };
    } catch (e) {
        return { writable: false };
    }
}

/** Firefox-style alternate IndexedDB open when StorageManager.getDirectory is absent. */
function probeIndexedDbAlternateOpen() {
    return new Promise((resolve) => {
        try {
            const request = indexedDB.open('inPrivate');
            request.onerror = (event) => {
                if (request.error && request.error.name === 'InvalidStateError') {
                    event.preventDefault();
                }
                resolve({ alternateDbOpened: false });
            };
            request.onsuccess = () => {
                try {
                    indexedDB.deleteDatabase('inPrivate');
                } catch (e) { /* ignore */ }
                resolve({ alternateDbOpened: true });
            };
            request.onblocked = () => resolve({ alternateDbOpened: true });
        } catch (e) {
            resolve({ alternateDbOpened: false });
        }
    });
}

/** Legacy Chromium temporary filesystem (Chrome ~50–75). */
function probeWebkitTemporaryFileSystem() {
    return new Promise((resolve) => {
        const w = typeof window !== 'undefined' ? window : {};
        const fs = w.webkitRequestFileSystem;
        if (typeof fs !== 'function') {
            resolve(null);
            return;
        }
        try {
            fs.call(w, 0, 1, () => resolve({ accessible: true }), () => resolve({ accessible: false }));
        } catch (e) {
            resolve({ accessible: false });
        }
    });
}

export function isOpfsSupported() {
    return typeof navigator !== 'undefined' && typeof navigator.storage?.getDirectory === 'function';
}

export async function runPhase1StorageProbes() {
    const [indexedDB, opfsState] = await Promise.all([openIndexedDbTest(), resolveOpfsRootOnce()]);

    const opfsSupported = isOpfsSupported();
    const storage = {
        opfsSupported,
        opfsDirectory: {
            granted: !!opfsState.root,
            errorTag: opfsState.root ? null : opfsErrorTag(opfsState.error),
        },
    };

    const hasAllSettled = typeof Promise !== 'undefined' && typeof Promise.allSettled === 'function';
    if (!hasAllSettled) {
        storage.webkitTemporaryFileSystem = await probeWebkitTemporaryFileSystem();
    }

    if (!opfsSupported && typeof navigator !== 'undefined' && navigator.maxTouchPoints !== undefined) {
        storage.indexedDbBlobStore = await probeIndexedDbBlobStore();
    }

    if (!opfsSupported && typeof navigator !== 'undefined' && navigator.maxTouchPoints === undefined) {
        storage.legacyWebSql = probeOpenDatabase();
        storage.localStorage = probeLocalStorageWritable();
    }

    if (!opfsSupported) {
        storage.indexedDbAlternateOpen = await probeIndexedDbAlternateOpen();
    }

    return {
        indexedDB,
        fileSystem: !!opfsState.root,
        storage,
    };
}

const FAST_STORAGES = ['localStorage', 'cookie'];
const SLOW_STORAGES = [
    // Disabled for marker recovery: production stats show high latency and very low recovery value.
    // OPFS is kept because it is harder to clear with standard browser UI than Cache API / IndexedDB.
    // 'cacheAPI',
    // 'indexedDB',
    'opfs',
];
const MARKER_STORAGES = [...FAST_STORAGES, ...SLOW_STORAGES];
const STORAGE_ADAPTERS = {
    localStorage: {
        read: (storage, key) => localStorageGetItemSafe(key),
        write: (storage, key, value) => localStorageSetItemSafe(key, value),
    },
    cookie: {
        read: (storage, key) => storage.getCookie(key),
        write: (storage, key, value) => {
            storage.setCookie(key, value, 365);
            return storage.getCookie(key) === value;
        },
    },
    cacheAPI: {
        read: (storage, key) => storage.getCacheAPI(key),
        write: (storage, key, value) => storage.storeCacheAPI(key, value),
    },
    indexedDB: {
        read: (storage, key) => storage.getIndexedDB(key),
        write: (storage, key, value) => storage.storeIndexedDB(key, value),
    },
    opfs: {
        read: (storage, key) => storage.getOPFS(key),
        write: (storage, key, value) => storage.storeOPFS(key, value),
    },
};

class PersistentStorage {
    storage = 'default';

    constructor(storage) {
        this.storage = storage;
    }

    async store(key, valueOrFactory) {
        const signingKey = config._k;
        const details = {
            createdNew: false,
            resolvedFrom: null,
        };
        const observed = {};
        const confirmedMarkers = {};
        let newTokenPromise = null;
        const getNewToken = () => {
            if (!newTokenPromise) {
                newTokenPromise = Promise.resolve(
                    typeof valueOrFactory === 'function' ? valueOrFactory() : valueOrFactory
                );
            }
            return newTokenPromise;
        };

        const fastSelection = await this.selectFromFastStorages(key, signingKey, details, observed, confirmedMarkers);
        if (fastSelection) {
            return this.finalizeMarkerSelection(key, fastSelection, observed, confirmedMarkers, details);
        }

        const slowSelection = await this.selectFromSlowStorages(key, signingKey, details, observed, confirmedMarkers);
        if (slowSelection) {
            return this.finalizeMarkerSelection(key, slowSelection, observed, confirmedMarkers, details);
        }

        const token = await getNewToken();
        return this.finalizeMarkerSelection(key, {
            token,
            resolvedFrom: 'new',
            createdNew: true,
        }, observed, confirmedMarkers, details);
    }

    async selectFromFastStorages(key, signingKey, details, observed, confirmedMarkers) {
        for (const storageName of FAST_STORAGES) {
            const token = this.readTimed(storageName, key, details);
            observed[storageName] = token;

            if (!token) continue;
            if (await this.verifyToken(token, signingKey) === true) {
                confirmedMarkers[storageName] = token;
                return {
                    token,
                    resolvedFrom: storageName,
                    createdNew: false,
                };
            }
        }

        return null;
    }

    async selectFromSlowStorages(key, signingKey, details, observed, confirmedMarkers) {
        const pending = new Map(
            SLOW_STORAGES.map((storageName) => [
                storageName,
                Promise.resolve(this.readTimed(storageName, key, details)).then((token) => ({ storageName, token })),
            ])
        );

        while (pending.size > 0) {
            const result = await Promise.race(pending.values());
            pending.delete(result.storageName);
            observed[result.storageName] = result.token;

            if (!result.token) continue;
            if (await this.verifyToken(result.token, signingKey) === true) {
                confirmedMarkers[result.storageName] = result.token;
                return {
                    token: result.token,
                    resolvedFrom: result.storageName,
                    createdNew: false,
                };
            }
        }

        return null;
    }

    finalizeMarkerSelection(key, selection, observed, confirmedMarkers, details) {
        details.createdNew = selection.createdNew === true;
        details.resolvedFrom = selection.resolvedFrom;

        this.ensureFastStorages(key, selection.token, observed, confirmedMarkers, details);
        this.startSlowReconcile(key, selection.token, details);

        const markers = this.markerPayload(selection.token, selection.createdNew, confirmedMarkers);
        return { markers, details };
    }

    ensureFastStorages(key, validToken, observed, confirmedMarkers, details) {
        for (const storageName of FAST_STORAGES) {
            if (observed[storageName] === validToken) continue;

            const ok = this.writeTimed(storageName, key, validToken, details);
            if (ok === true) {
                confirmedMarkers[storageName] = validToken;
            }
        }
    }

    startSlowReconcile(key, validToken, details) {
        for (const storageName of SLOW_STORAGES) {
            this.backgroundReconcile(storageName, key, validToken, details);
        }
    }

    backgroundReconcile(storageName, key, validToken, details) {
        void (async () => {
            const existing = await this.readRaw(storageName, key);
            if (existing === validToken) return;

            await this.recordTiming(details, `bgSync.${storageName}.set`, () => this.writeRaw(storageName, key, validToken));
        })().catch(() => {});
    }

    markerPayload(validToken, isNew, confirmedMarkers) {
        const payload = { new: isNew };
        for (const storageName of MARKER_STORAGES) {
            if (confirmedMarkers[storageName] === validToken) {
                payload[storageName] = validToken;
            }
        }
        return payload;
    }

    readTimed(storageName, key, details, prefix = 'get') {
        return this.recordTiming(details, `${prefix}.${storageName}`, () => this.readRaw(storageName, key));
    }

    writeTimed(storageName, key, value, details, prefix = 'set') {
        return this.recordTiming(details, `${prefix}.${storageName}`, () => this.writeRaw(storageName, key, value));
    }

    readRaw(storageName, key) {
        const adapter = STORAGE_ADAPTERS[storageName];
        return adapter ? adapter.read(this, key) : null;
    }

    writeRaw(storageName, key, value) {
        const adapter = STORAGE_ADAPTERS[storageName];
        return adapter ? adapter.write(this, key, value) : false;
    }

    recordTiming(details, name, fn) {
        try {
            const result = withTiming(name, details, fn);
            if (result && typeof result.then === 'function') {
                return result.catch(() => null);
            }
            return result;
        } catch (error) {
            return null;
        }
    }

    async verifyToken(token, signingKey) {
        if (!token || typeof token !== 'string') return false;

        const [payloadB64, sigB64] = token.split(".");
        if (!payloadB64 || !sigB64) return false;

        try {
            const payload = Uint8Array.from(atob(payloadB64), c => c.charCodeAt(0));
            const signature = Uint8Array.from(atob(sigB64), c => c.charCodeAt(0));

            const key = await crypto.subtle.importKey(
                "raw",
                new TextEncoder().encode(signingKey),
                { name: "HMAC", hash: "SHA-256" },
                false,
                ["verify"]
            );

            return await crypto.subtle.verify(
                "HMAC",
                key,
                signature,
                payload
            );
        } catch (error) {
            return false;
        }
    }

    getCacheAPI(key) {
        const cacheName = this.storage;

        return cacheGet(key);
        async function cacheGet( key ) {
            try {
                const cache = await caches.open( cacheName ),
                        resp = await cache.match(key);
                if (!resp) return null;
                return await resp.text();
            }
            catch(err) {
                return null;
            }
        }
    }

    async storeCacheAPI(key, value) {
        const cacheName = this.storage;

        try {
            let response = new Response(value);
            const cache = await caches.open(cacheName);
            await cache.put(key, response);
            return true;
        } catch (err) {
            return false;
        }
    }

    setCookie(name,value,days) {
        var expires = "";
        if (days) {
            var date = new Date();
            date.setTime(date.getTime() + (days*24*60*60*1000));
            expires = "; expires=" + date.toUTCString();
        }
        document.cookie = name + "=" + (value || "")  + expires + "; path=/";
    }

    getCookie(name) {
        var nameEQ = name + "=";
        var ca = document.cookie.split(';');
        for(var i=0;i < ca.length;i++) {
            var c = ca[i];
            while (c.charAt(0)==' ') c = c.substring(1,c.length);
            if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length,c.length);
        }
        return null;
    }

    async storeIndexedDB(key, value) {
        try {
            await set(key, value);
            return true;
        } catch (error) {
            return false;
        }
    }

    async getIndexedDB(key) {
        try {
            return await get(key) || null;
        } catch (error) {
            return null;
        }
    }

    async getOPFS(key) {
        try {
            if (!('FileSystemHandle' in window) || typeof navigator.storage?.getDirectory !== 'function') {
                return null;
            }

            const { root } = await resolveOpfsRootOnce();
            if (!root) {
                return null;
            }

            const fileHandle = await root.getFileHandle(key, { create: false });
            const file = await fileHandle.getFile();
            return await file.text();
        } catch (error) {
            // File doesn't exist or OPFS not supported
            return null;
        }
    }

    async storeOPFS(key, value) {
        try {
            if (!('FileSystemHandle' in window) || typeof navigator.storage?.getDirectory !== 'function') {
                return false;
            }

            const { root } = await resolveOpfsRootOnce();
            if (!root) {
                return false;
            }

            const fileHandle = await root.getFileHandle(key, { create: true });
            const writable = await fileHandle.createWritable();
            await writable.write(value);
            await writable.close();
            return true;
        } catch (error) {
            return false;
        }
    }
}

export { PersistentStorage };
